<template>
  <div class="border rounded-lg bg-white">
    <section
      class="selection-summary p-3 border-bottom"
      aria-labelledby="selection-summary-title"
      aria-live="polite"
    >
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <h4 id="selection-summary-title" class="h5 mb-1">
            Estados e Municípios
          </h4>
          <p class="text-muted mb-0" v-text="selectionSummaryText" />
        </div>

        <b-button
          v-if="hasSelectedValue"
          variant="outline-danger"
          size="sm"
          class="flex-shrink-0 rounded-pill"
          :disabled="disabled"
          @click="removeAll"
        >
          Limpar seleção
        </b-button>
      </div>
    </section>

    <b-row no-gutters>
      <b-col cols="12" lg="4" class="p-3 border-right">
        <select-multi-municipio-section
          disable-search
          title="Região"
          subtitle="Selecione para filtrar os estados"
          :disabled="disabled || loading"
          :show-count="showColumnCounters"
          :count-label="regionFooterText"
        >
          <b-skeleton-wrapper :loading="loading">
            <template #loading>
              <b-skeleton
                v-for="index in 4"
                :key="index"
                width="100%"
                height="2.25em"
                class="rounded-lg mb-3 text-gray"
              />
            </template>
            <select-multi-municipio-list-item
              v-for="region in filteredRegions"
              :key="region.regiao"
              :active="selectedRegionValue === region.regiao"
              :disabled="disabled"
              :icon="selectionActionLabel('regiao', region.regiao)"
              :coverage-label="coverageLabel('regiao', region.regiao)"
              :toggle-label="
                toggleSelectionLabel('regiao', region.regiao, region.nome)
              "
              show-navigation-icon
              @select="selectRegion(region)"
              @toggle="toggleSelection('regiao', region.regiao)"
            >
              {{ region.nome }}
            </select-multi-municipio-list-item>
          </b-skeleton-wrapper>
        </select-multi-municipio-section>
      </b-col>

      <b-col cols="12" lg="4" class="p-3 border-right">
        <select-multi-municipio-section
          disable-search
          :disabled="disabled || loading || !hasStateItems"
          :title="stateTitle"
          :show-count="showColumnCounters"
          :count-label="stateFooterText"
          subtitle="Escolha uma região primeiro"
        >
          <b-skeleton-wrapper :loading="loading">
            <template #loading>
              <b-skeleton
                v-for="index in 4"
                :key="index"
                width="100%"
                height="2.25em"
                class="rounded-lg mb-3 text-gray"
              />
            </template>
            <b-list-group-item
              v-if="!selectedRegion && !hasStateItems"
              class="text-center text-muted"
            >
              Selecione uma região.
            </b-list-group-item>
            <select-multi-municipio-list-item
              v-for="state in filteredStates"
              v-else
              :key="state.sigla"
              :active="selectedStateValue === state.sigla"
              :disabled="disabled"
              :icon="selectionActionLabel('estado', state.sigla)"
              :coverage-label="coverageLabel('estado', state.sigla)"
              :toggle-label="
                toggleSelectionLabel('estado', state.sigla, state.nome)
              "
              show-navigation-icon
              @select="selectState(state)"
              @toggle="toggleSelection('estado', state.sigla)"
            >
              {{ state.nome }} / {{ state.sigla }}
            </select-multi-municipio-list-item>
          </b-skeleton-wrapper>
        </select-multi-municipio-section>
      </b-col>

      <b-col cols="12" lg="4" class="p-3">
        <select-multi-municipio-section
          v-model="searchMunicipality"
          :disabled="disabled || loading"
          :list-group="false"
          :title="municipalityTitle"
          :show-count="showColumnCounters"
          :count-label="municipalityFooterText"
          subtitle="Adicione municípios avulsos"
          placeholder="Buscar município em todo o Brasil..."
        >
          <b-skeleton-wrapper :loading="loading">
            <template #loading>
              <b-skeleton
                v-for="index in 4"
                :key="index"
                width="100%"
                height="2.25em"
                class="rounded-lg mb-3 text-gray"
              />
            </template>
            <b-list-group v-if="municipalityCount === 0">
              <b-list-group-item class="text-center text-muted">
                {{ municipalityEmptyMessage }}
              </b-list-group-item>
            </b-list-group>
            <recycle-scroller
              v-else
              :items="filteredMunicipalities"
              :item-size="58"
              key-field="id"
              class="border rounded overflow-auto"
              style="height: 19rem"
            >
              <template #default="{ item: municipality }">
                <select-multi-municipio-list-item
                  :key="municipality.id"
                  tag="div"
                  :disabled="disabled"
                  :inherited="
                    isInheritedSelection('municipio', municipality.id)
                  "
                  :icon="selectionActionLabel('municipio', municipality.id)"
                  :toggle-label="
                    toggleSelectionLabel(
                      'municipio',
                      municipality.id,
                      municipality.text
                    )
                  "
                  item-class="border-bottom bg-white"
                  item-style="height: 58px"
                  @select="toggleSelection('municipio', municipality.id)"
                  @toggle="toggleSelection('municipio', municipality.id)"
                >
                  {{ municipality.text }}
                </select-multi-municipio-list-item>
              </template>
            </recycle-scroller>
          </b-skeleton-wrapper>
        </select-multi-municipio-section>
      </b-col>
    </b-row>

    <b-alert v-if="isError" show variant="danger" class="m-3">
      Não foi possível carregar os territórios.
      <b-button variant="link" class="p-0" @click="refetch">
        Tentar novamente
      </b-button>
    </b-alert>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from "vue";
import services from "@/http";
import { useFuse } from "@/composables/useFuse";
import { useQuery } from "@/composables/useQuery.js";
import { safeArray } from "@/helpers/functions/ArrayHelpers";
import { useTerritorySelectionPresentation } from "@/modules/oportunidades/composables/useTerritorySelectionPresentation";
import {
  createTerritoryCatalog,
  getMunicipalitiesByRegion,
  getMunicipalitiesByState,
  getStatesByRegion,
  normalizeMunicipalityValue,
  normalizeStateValue,
} from "@/modules/oportunidades/domain/territoryCatalog";
import {
  compactTerritorySelection,
  createTerritoryCoverage,
  emptyTerritorySelection,
  getTerritoryCoverage,
  isInheritedMunicipalitySelection,
  isTerritorySelected,
  removeTerritorySelection,
  toggleTerritorySelection,
} from "@/modules/oportunidades/domain/territorySelection";

export default defineComponent({
  name: "SelectMultiMunicipioOportunidade",
  components: {
    SelectMultiMunicipioListItem: () =>
      import("@/modules/users/Components/SelectMultiMunicipioListItem.vue"),

    SelectMultiMunicipioSection: () =>
      import("@/modules/users/Components/SelectMultiMunicipioSection.vue"),
  },
  props: {
    value: {
      type: Object,
      default: emptyTerritorySelection,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["input", "change"],
  setup(props, { emit }) {
    const selectedRegionValue = ref(null);
    const selectedStateValue = ref(null);
    const searchRegion = ref("");
    const searchState = ref("");
    const searchMunicipality = ref("");

    const {
      data: territories,
      isFetching: loading,
      isError,
      refetch,
    } = useQuery({
      key: ["select-municipios"],
      queryFn: () => services.users.territorios(),
      shallow: true,
      initialData: () => createTerritoryCatalog([]),
      select: (response) => createTerritoryCatalog(safeArray(response.data)),
    });

    const modelValue = computed({
      get() {
        return compactTerritorySelection(props.value, territories.value);
      },
      set(value) {
        const normalizedValue = compactTerritorySelection(
          value,
          territories.value
        );

        emit("input", normalizedValue);
        emit("change", normalizedValue);
      },
    });

    const regions = computed(() =>
      Array.from(territories.value.regioes.values())
    );
    const selectedRegion = computed(() =>
      territories.value.regioes.get(String(selectedRegionValue.value))
    );
    const selectedState = computed(() =>
      territories.value.estados.get(String(selectedStateValue.value))
    );
    const territoryCoverage = computed(() =>
      createTerritoryCoverage(modelValue.value, territories.value)
    );
    const isMunicipalitySearchActive = computed(() =>
      Boolean(String(searchMunicipality.value || "").trim())
    );

    const states = computed(() => {
      if (selectedRegion.value) {
        return getStatesByRegion(territories.value, selectedRegionValue.value);
      }

      return Array.from(territories.value.estados.values()).filter((state) => {
        const coverage = getTerritoryCoverage(
          territoryCoverage.value,
          "estado",
          state.sigla
        );

        return (
          coverage.selected > 0 ||
          territoryCoverage.value.selectedStateValues.has(state.sigla)
        );
      });
    });

    const municipalities = computed(() => {
      if (isMunicipalitySearchActive.value) {
        return Array.from(territories.value.municipios.values());
      }

      if (selectedState.value) {
        return getMunicipalitiesByState(
          territories.value,
          selectedStateValue.value
        );
      }

      if (selectedRegion.value) {
        return getMunicipalitiesByRegion(
          territories.value,
          selectedRegionValue.value
        );
      }

      return Array.from(territories.value.municipios.values()).filter(
        (municipality) =>
          territoryCoverage.value.effectiveMunicipalityValues.has(
            municipality.id
          )
      );
    });

    const hasStateItems = computed(() => states.value.length > 0);

    const { list: filteredRegions, clear: clearRegionSearch } = useFuse(
      regions,
      searchRegion,
      {
        keys: ["nome", "regiao"],
        threshold: 0.3,
      }
    );
    const { list: filteredStates, clear: clearStateSearch } = useFuse(
      states,
      searchState,
      {
        keys: ["nome", "sigla"],
        threshold: 0.3,
      }
    );
    const {
      list: filteredMunicipalities,
      clear: clearMunicipalitySearch,
      count: municipalityCount,
    } = useFuse(municipalities, searchMunicipality, {
      keys: ["text", "searchText", "uf"],
      threshold: 0.3,
    });

    const {
      hasSelectedValue,
      municipalityEmptyMessage,
      municipalityFooterText,
      municipalityTitle,
      regionFooterText,
      selectionSummaryText,
      showColumnCounters,
      stateFooterText,
      stateTitle,
      coverageLabel,
      selectionActionLabel,
      toggleSelectionLabel,
    } = useTerritorySelectionPresentation({
      filteredMunicipalities,
      filteredRegions,
      filteredStates,
      isError,
      isMunicipalitySearchActive,
      isSelected,
      loading,
      modelValue,
      regions,
      selectedRegion,
      selectedState,
      territoryCoverage,
    });

    const selectedValue = computed(() => {
      const result = [];
      const statesSummarizedByRegion = new Set();

      for (const region of territories.value.regioes.values()) {
        if (!isSelected("regiao", region.regiao)) continue;

        for (const state of getStatesByRegion(
          territories.value,
          region.regiao
        )) {
          statesSummarizedByRegion.add(state.sigla);
        }

        result.push({
          key: `regiao-${region.regiao}`,
          type: "regiao",
          value: region.regiao,
          label: `Região ${region.nome || region.regiao}`,
        });
      }

      for (const state of modelValue.value.estados) {
        const stateValue = normalizeStateValue(state);
        if (statesSummarizedByRegion.has(stateValue)) continue;

        const mappedState = territories.value.estados.get(stateValue);
        const stateName = mappedState?.nome || mappedState?.text || stateValue;

        result.push({
          key: `estado-${stateValue}`,
          type: "estado",
          value: stateValue,
          label: `${stateName} · ${stateValue}`,
        });
      }

      for (const municipality of modelValue.value.municipios) {
        const municipalityValue = normalizeMunicipalityValue(municipality);
        const mappedMunicipality = territories.value.municipios.get(
          String(municipalityValue)
        );
        const municipalityName =
          mappedMunicipality?.text ||
          mappedMunicipality?.nome ||
          municipalityValue;
        const municipalityUf = mappedMunicipality?.uf || "";

        result.push({
          key: `municipio-${municipalityValue}`,
          type: "municipio",
          value: municipalityValue,
          label: `${municipalityName} - ${municipalityUf}`,
        });
      }

      return result;
    });

    function isInheritedSelection(level, value) {
      if (level !== "municipio") return false;

      return isInheritedMunicipalitySelection(
        modelValue.value,
        value,
        territories.value
      );
    }

    function isSelected(level, value) {
      return isTerritorySelected(
        level,
        value,
        modelValue.value,
        territories.value,
        territoryCoverage.value
      );
    }

    function selectRegion(region) {
      if (props.disabled) return;

      const regionValue = String(region?.regiao ?? "");
      if (!regionValue) return;

      const isActiveRegion = selectedRegionValue.value === regionValue;
      const hasActiveState = selectedStateValue.value !== null;
      const existsRegion = isActiveRegion && !hasActiveState;

      selectedRegionValue.value = existsRegion ? null : regionValue;
      selectedStateValue.value = null;
      clearStateSearch();
      clearMunicipalitySearch();
    }

    function selectState(state) {
      if (props.disabled) return;

      const stateValue = normalizeStateValue(state);
      const regionValue = String(state?.regiao ?? "");
      if (!stateValue || !regionValue) return;

      const isActiveState = selectedStateValue.value === stateValue;

      selectedRegionValue.value = regionValue;
      selectedStateValue.value = isActiveState ? null : stateValue;
      clearMunicipalitySearch();
    }

    function toggleSelection(level, value) {
      if (props.disabled) return;

      modelValue.value = toggleTerritorySelection(
        level,
        value,
        modelValue.value,
        territories.value
      );

      if (level === "municipio" && isMunicipalitySearchActive.value) {
        clearMunicipalitySearch();
      }
    }

    function removeAll() {
      if (props.disabled) return;

      selectedRegionValue.value = null;
      selectedStateValue.value = null;
      clearRegionSearch();
      clearStateSearch();
      clearMunicipalitySearch();
      modelValue.value = emptyTerritorySelection();
    }

    function removeSelectedValue(item) {
      if (props.disabled) return;

      modelValue.value = removeTerritorySelection(
        item.type,
        item.value,
        modelValue.value,
        territories.value
      );
    }

    return {
      filteredMunicipalities,
      filteredRegions,
      filteredStates,
      hasSelectedValue,
      hasStateItems,
      isError,
      loading,
      municipalityCount,
      municipalityEmptyMessage,
      municipalityFooterText,
      municipalityTitle,
      regionFooterText,
      searchMunicipality,
      searchRegion,
      searchState,
      selectedRegion,
      selectedRegionValue,
      selectedState,
      selectedStateValue,
      selectedValue,
      selectionSummaryText,
      showColumnCounters,
      stateFooterText,
      stateTitle,

      coverageLabel,
      isInheritedSelection,
      refetch,
      removeAll,
      removeSelectedValue,
      selectionActionLabel,
      selectRegion,
      selectState,
      toggleSelection,
      toggleSelectionLabel,
    };
  },
});
</script>

<style scoped lang="scss">
.selection-chip {
  border-radius: 999px;
  white-space: normal;
  text-align: left;
}

.selection-summary-toggle {
  min-height: 31px;
}
</style>
